<template>
  <div class="travel-route-list">
    <SearchBar
      :default-search="searchKeyword"
      @search="handleSearch"
      @city-change="handleCityChange"
      @search-cleared="handleSearchCleared"
    />
    <!-- 筛选区域 -->
    <div class="filter-section">
      <!-- 出行方式筛选 -->
      <div class="filter-group">
        <div class="filter-row">
          <div class="filter-label-col">
            <div class="filter-label">{{ $t("travelMode") }}</div>
          </div>
          <div class="filter-options-col">
            <div class="filter-options">
              <div
                class="option-item"
                :class="{ selected: filters.travelType === null }"
                @click="updateFilter('travelType', null)"
              >
                {{ $t("all") }}
              </div>
              <div
                v-for="option in travelTypeOptions"
                :key="option.id"
                :class="[
                  'option-item',
                  filters.travelType === option.id ? 'selected' : '',
                ]"
                @click="updateFilter('travelType', option)"
              >
                {{ $i18n.locale === "zh" ? option.name : option.name_en }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- 行程天数筛选 -->
      <div class="filter-group">
        <div class="filter-row">
          <div class="filter-label-col">
            <div class="filter-label">{{ $t("tripDays") }}</div>
          </div>
          <div class="filter-options-col">
            <div class="filter-options">
              <div
                :class="{
                  'option-item': true,
                  selected: filters.tripDays === null,
                }"
                @click="updateFilter('tripDays', null)"
              >
                {{ $t("all") }}
              </div>
              <div
                v-for="day in dayOptions"
                :key="day.id"
                :class="{
                  'option-item': true,
                  selected: filters.tripDays === day.id,
                }"
                @click="updateFilter('tripDays', day)"
              >
                {{ $i18n.locale === "zh" ? day.name : day.name_en }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- 成团人数筛选 -->
      <div class="filter-group">
        <div class="filter-row">
          <div class="filter-label-col">
            <div class="filter-label">{{ $t("groupSize") }}</div>
          </div>
          <div class="filter-options-col">
            <div class="filter-options">
              <div
                :class="{
                  'option-item': true,
                  selected: filters.groupSize === null,
                }"
                @click="updateFilter('groupSize', null)"
              >
                {{ $t("all") }}
              </div>
              <div
                v-for="option in groupSizeOptions"
                :key="option.id"
                :class="{
                  'option-item': true,
                  selected: filters.groupSize === option.id,
                }"
                @click="updateFilter('groupSize', option)"
              >
                {{ $i18n.locale === "zh" ? option.label : option.label_en }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- 游玩主题筛选 -->
      <div class="filter-group theme-filter">
        <div class="filter-row">
          <div class="filter-label-col">
            <div class="filter-label">{{ $t("playTheme") }}</div>
          </div>
          <div class="filter-options-col">
            <div class="theme-options-wrapper">
              <div class="filter-options theme-options">
                <div
                  class="option-item"
                  :class="{ selected: !filters.themes.length }"
                  @click="toggleTheme('不限')"
                >
                  {{ $t("all") }}
                </div>
                <div
                  v-for="theme in visibleThemeOptions"
                  :key="theme.id"
                  :class="[
                    'option-item',
                    // 用 id 判断选中
                    filters.themes.includes(theme.id) ? 'selected' : '',
                  ]"
                  @click="toggleTheme(theme)"
                >
                  {{ $i18n.locale === "zh" ? theme.name : theme.name_en }}
                </div>
              </div>
              <div
                v-if="hasMoreThemes"
                class="toggle-theme-btn"
                @click="toggleThemeExpand"
              >
                {{ isThemeExpanded ? $t("collapse") : $t("expand") }}
                <i class="el-icon-arrow-up" v-if="isThemeExpanded"></i>
                <i class="el-icon-arrow-down" v-else></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 价格范围筛选 -->
      <div class="filter-group price-filter">
        <div class="filter-row">
          <div class="filter-label-col">
            <div class="filter-label">{{ $t("priceRange") }}</div>
          </div>
          <div class="filter-options-col">
            <div class="price-filter-container">
              <!-- 默认价格区间选项 - 点击直接筛选 -->
              <div class="price-ranges">
                <div
                  class="price-range-option"
                  :class="{ selected: activePriceRangeKey === null }"
                  @click="selectPriceRange(null)"
                >
                  {{ $t("all") }}
                </div>
                <div
                  v-for="range in defaultPriceRanges"
                  :key="range.key"
                  :class="[
                    'price-range-option',
                    activePriceRangeKey === range.key ? 'selected' : '',
                  ]"
                  @click="selectPriceRange(range)"
                >
                  {{ $i18n.locale === "zh" ? range.label : range.label_en }}
                </div>
              </div>
              <div class="custom-price-input-group">
                <el-input
                  size="small"
                  v-model="priceRange[0]"
                  :placeholder="$t('minPrice')"
                  type="number"
                  class="price-input"
                  @keydown.enter.prevent
                  @change="clearCustomSelection"
                ></el-input>
                <span class="price-separator">-</span>
                <el-input
                  size="small"
                  v-model="priceRange[1]"
                  :placeholder="$t('maxPrice')"
                  type="number"
                  class="price-input"
                  @keydown.enter.prevent
                  @change="clearCustomSelection"
                >
                </el-input>
                <span class="price-unit">{{ $t("yuan") }}</span>
                <el-button
                  type="primary"
                  size="mini"
                  class="price-confirm-btn"
                  @click="updateCustomPriceRange"
                >
                  {{ $t("confirm") }}
                </el-button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- 筛选操作区 -->
      <div class="filter-actions">
        <button
          class="clear-filters"
          @click="clearAllFilters"
          :disabled="isAllFiltersCleared"
        >
          <i class="el-icon-refresh-left"></i> {{ $t("clearFilters") }}
        </button>
      </div>
      <!-- 已选筛选条件标签 -->
      <div class="active-filters" v-if="activeFilters?.length || 0">
        <span class="active-label">{{ $t("selected") }}：</span>
        <!-- <el-tag
          v-for="filter in activeFilters"
          :key="filter.key + filter.value"
          closable
          @close="removeFilter(filter.key, filter.value)"
        >
          {{ filter.label }}: {{ filter.value }}
        </el-tag> -->
        <el-tag
          v-for="filter in activeFilters"
          :key="filter.key + (filter.id ?? filter.value)"
          closable
          @close="removeFilter(filter.key, filter.id ?? filter.value)"
        >
          {{ filter.label }}:
          <!-- 出行方式 -->
          <span v-if="filter.key === 'travelType'">
            {{
              $i18n.locale === "zh"
                ? travelTypeOptions.find((o) => o.id === filter.id)?.name
                : travelTypeOptions.find((o) => o.id === filter.id)?.name_en
            }}
          </span>
          <!-- 行程天数 -->
          <span v-else-if="filter.key === 'tripDays'">
            {{
              $i18n.locale === "zh"
                ? dayOptions.find((o) => o.id === filter.id)?.name
                : dayOptions.find((o) => o.id === filter.id)?.name_en
            }}
          </span>
          <!-- 成团人数 -->
          <span v-else-if="filter.key === 'groupSize'">
            {{
              $i18n.locale === "zh"
                ? groupSizeOptions.find((o) => o.id === filter.id)?.label
                : groupSizeOptions.find((o) => o.id === filter.id)?.label_en
            }}
          </span>
          <!-- 游玩主题 -->
          <span v-else-if="filter.key === 'themes'">
            {{
              $i18n.locale === "zh"
                ? themeOptions.find((o) => o.id === filter.id)?.name
                : themeOptions.find((o) => o.id === filter.id)?.name_en
            }}
          </span>
          <!-- 其他筛选类型沿用原有value -->
          <span v-else>{{ filter.value }}</span>
        </el-tag>
      </div>
    </div>
    <!-- 主要内容区域 -->
    <div class="main-content">
      <!-- 列表控制和结果区域 -->
      <div class="list-section">
        <!-- 排序和结果统计 -->
        <div class="list-header">
          <div class="result-count">
            {{ routes?.length || 0 }}{{ $t("itemsFound") }}
          </div>
          <div class="sort-container">
            <el-select
              v-model="sortBy"
              :placeholder="$t('sortBy')"
              @change="handleSortChange"
              class="sort-select"
            >
              <el-option :label="$t('publishDate')" value="date"></el-option>
              <el-option :label="$t('sales')" value="sales"></el-option>
              <el-option :label="$t('price')" value="price"></el-option>
            </el-select>
          </div>
        </div>
        <!-- 路线列表 -->
        <div class="route-list" v-if="routes?.length || 0">
          <div
            v-for="(route, index) in routes"
            :key="index"
            class="route-item"
            @click="toRouteDetail(route.id)"
          >
            <div class="route-image" @click.stop>
              <el-image
                :src="route.cover_image"
                :preview-src-list="route.images"
                fit="cover"
              ></el-image>
              <div v-if="route.tag" class="route-tag">
                <svg class="icon" aria-hidden="true">
                  <use
                    v-if="route.tag === '新品'"
                    xlink:href="#icon-xinpin3"
                  ></use>
                  <use
                    v-if="route.tag === '热销'"
                    xlink:href="#icon-rexiao"
                  ></use>
                  <use
                    v-if="route.tag === '好评'"
                    xlink:href="#icon-haoping-yuankuang-copy"
                  ></use>
                </svg>
              </div>
            </div>
            <div class="route-info">
              <span class="route-title">{{ route.routeName }}</span>
              <div class="route-meta">
                <span>{{ route.departure }}{{ $t("departure") }}</span>
                <span>{{ route.days }}</span>
                <span>{{ route.travel_type_text }}</span>
              </div>
              <div class="route-features">
                <el-tag
                  v-for="(feature, i) in route.themes"
                  :key="i"
                  size="small"
                >
                  {{ feature }}
                </el-tag>
              </div>
              <div class="route-price">
                <span class="price-value">¥{{ route.price }}</span>
                <span class="price-unit">{{ $t("perPerson") }}</span>
              </div>
              <div class="route-stats">
                <span>{{ route.rating }}{{ $t("score") }}</span>
                <span>{{ $t("sold") }} {{ route.sales }}</span>
              </div>
            </div>
          </div>
        </div>
        <!-- 无结果提示 -->
        <div v-else class="no-result">
          <el-empty :description="$t('noResult')"></el-empty>
        </div>
        <!-- 分页 -->
        <div class="pagination-container" v-if="routes?.length || 0">
          <Pagination
            :total="totalCount"
            :page-size="pageSize"
            :current-page="currentPage"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </div>
      <!-- 右侧边栏 -->
      <div class="sidebar">
        <!-- 城市介绍 -->
        <div class="city-info">
          <h3 class="section-title">
            {{ cityInfo.city }}{{ $t("travelGuide") }}
          </h3>
          <div class="city-image">
            <el-image
              :src="cityInfo.representative_image"
              fit="cover"
            ></el-image>
          </div>
          <div class="city-description">
            <p>
              {{
                $i18n.locale === "en"
                  ? cityInfo.introduction_en
                  : cityInfo.introduction
              }}
            </p>
          </div>
        </div>
        <!-- 热销产品 -->
        <div class="hot-products">
          <h3 class="section-title">{{ $t("hotTop5") }}</h3>
          <div
            v-for="(product, index) in hotProducts"
            :key="index"
            class="hot-product-item"
            @click="toRouteDetail(product.id)"
          >
            <div class="hot-product-rank">
              {{ index + 1 }}
            </div>
            <div class="hot-product-info">
              <h4 class="hot-product-title">{{ product.route_name }}</h4>
              <p class="hot-product-route">
                {{ product.departure }}-{{ product.destination }}/
                {{ product.days }}/
                {{ product.travel_type_text }}
              </p>
              <p class="hot-product-price">
                ¥{{ product.price }}{{ $t("perPerson") }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Pagination from "../../components/Pagination.vue";
import SearchBar from "../../components/SearchBar.vue"; // 导入搜索栏组件
export default {
  name: "TravelRouteListNew",
  components: {
    Pagination,
    SearchBar,
  },
  data() {
    return {
      searchKeyword: "",
      selectedCity: "",
      routes: [],
      filteredRoutes: [],
      loading: true, // 加载状态
      // 筛选条件
      filters: {
        travelType: null, // 出行方式：存储id（null表示不限）
        travelTypeName: null,
        tripDays: null, // 行程天数：存储id（null表示不限）
        themes: [], // 游玩主题：存储选中的name数组（后续拼接）
        groupSize: null, // 成团人数：存储id（null表示不限）
        groupSizeMin: null, // 成团人数最小
        groupSizeMax: null, // 成团人数最大
        tripDaysMin: null, // 成团人数最小
        tripDaysMax: null, // 成团人数最大
        priceRange: [null, null],
        priceMin: null, // 价格最小
        priceMax: null, // 价格最大
      },
      cityInfo: {},
      groupSizeOptions: [], // 成团人数区间选项
      defaultPriceRanges: [], // 默认价格区间
      priceRange: [null, null], // 价格范围双向绑定
      activePriceRangeKey: null, // 当前选中的价格区间key
      sortBy: "date", // 排序方式
      currentPage: 1, // 当前页码（默认第1页）
      pageSize: 10, // 每页条数（默认10条）
      // currentPage:
      //   Number(sessionStorage.getItem("travelRoute_currentPage")) || 1,
      // pageSize: Number(sessionStorage.getItem("travelRoute_pageSize")) || 10, // 默认10条/页
      totalCount: 0, // 总条数（由接口返回）
      travelTypeOptions: [], // 选项数据
      dayOptions: [],
      themeOptions: [],
      isThemeExpanded: false, // 控制游玩主题展开/收起
      themesPerRow: 12, // 一排能容纳的主题数量（根据实际布局计算）
      // 热销产品数据
      hotProducts: [],
    };
  },
  created() {
    const query = this.$route.query;
    if (query.keyword) {
      this.searchKeyword = query.keyword;
    }
    this.getStaticData();
    this.getHotProducts();
    this.getCityInfo();
    this.fetchFilteredRoutes();
  },
  watch: {
    // 监听路由变化
    $route(to) {
      // 当路由参数变化时，更新搜索关键词
      if (to.query.keyword) {
        this.searchKeyword = to.query.keyword;
      }
    },
    // 新增：监听语言变化
    "$i18n.locale"() {
      this.currentPage = 1;
      this.fetchFilteredRoutes();
      this.getHotProducts();
    },
  },
  computed: {
    // 根据展开状态和一排能容纳的数量过滤主题选项
    visibleThemeOptions() {
      if (this.isThemeExpanded) {
        return this.themeOptions;
      } else {
        // 收起状态下少显示一个位置给展开按钮
        return this.themeOptions.slice(0, Math.max(0, this.themesPerRow - 1));
      }
    },
    // 是否有更多主题未显示
    hasMoreThemes() {
      return this.themeOptions.length > this.themesPerRow;
    },
    // 检查是否所有筛选条件都是默认值
    isAllFiltersCleared() {
      return (
        this.filters.travelType === "不限" &&
        this.filters.tripDays === "不限" &&
        this.filters.themes.length === 0 &&
        this.filters.priceRange[0] === 0 &&
        this.filters.priceRange[1] === 10000 &&
        this.filters.groupSize === "不限"
      );
    },
    // 获取当前激活的筛选条件
    activeFilters() {
      const active = [];
      // 1.出行方式
      if (this.filters.travelType !== null) {
        const opt = this.travelTypeOptions.find(
          (item) => item.id === this.filters.travelType,
        );
        if (opt) {
          active.push({
            key: "travelType",
            label: this.$t("travelMode"),
            id: opt.id, // 存id，舍弃固定value文本
          });
        }
        // active.push({
        //   key: "travelType",
        //   label: this.$t("travelMode"),
        //   value: this.filters.travelTypeName,
        // });
      }
      // if (this.filters.tripDays !== null) {
      //   active.push({
      //     key: "tripDays",
      //     label: this.$t("tripDays"),
      //     value: this.filters.tripDays,
      //   });
      // }

      // 2.行程天数
      if (this.filters.tripDays !== null) {
        const dayOpt = this.dayOptions.find((item) => {
          // 原来filters.tripDays存的是name/name_en，现在updateFilter要改成存id，这里通过id匹配
          return item.id === this.filters.tripDays;
        });
        if (dayOpt) {
          active.push({
            key: "tripDays",
            label: this.$t("tripDays"),
            id: dayOpt.id,
          });
        }
      }
      // 3.主题 themes
      if (
        this.filters.themes && // 确保 themes 存在
        this.filters.themes.length > 0 &&
        !this.filters.themes.includes("不限")
      ) {
        this.filters.themes.forEach((themeId) => {
          active.push({
            key: "themes",
            label: this.$t("playTheme"),
            id: themeId, // 只存 id
          });
        });
      }
      // 4.成团人数
      if (this.filters.groupSize !== null) {
        const targetOption = this.groupSizeOptions.find(
          (opt) => opt.id === this.filters.groupSize,
        );
        if (targetOption) {
          active.push({
            key: "groupSize",
            label: this.$t("groupSize"),
            id: targetOption.id,
          });
        }
      }
      // 价格
      if (
        this.filters.priceRange &&
        Array.isArray(this.filters.priceRange) &&
        (this.filters.priceRange[0] !== null ||
          this.filters.priceRange[1] !== null)
      ) {
        const min =
          this.filters.priceRange[0] !== null ? this.filters.priceRange[0] : "";
        const max =
          this.filters.priceRange[1] !== null ? this.filters.priceRange[1] : "";
        if (max != 0) {
          active.push({
            key: "priceRange",
            label: this.$t("priceRange"),
            value: `${this.$t("currencySymbol")}${min} - ${this.$t(
              "currencySymbol",
            )}${max}${this.$t("yuan")}`,
          });
        } else {
          active.push({
            key: "priceRange",
            label: this.$t("priceRange"),
            value: `${min}${this.$t("yuanAbove")}`,
          });
        }
      }
      return active;
    },
  },
  methods: {
    fetchFilteredRoutes() {
      // 整理参数（包含分页和筛选条件）
      const params = {
        // 分页参数（必传）
        page: this.currentPage,
        pageSize: this.pageSize,
        // 筛选参数（与之前一致）
        keyword: this.searchKeyword || undefined,
        city: this.selectedCity || undefined,
        travelType:
          this.filters.travelType !== null
            ? this.filters.travelType
            : undefined,
        tripDaysMin:
          this.filters.tripDaysMin !== null
            ? this.filters.tripDaysMin
            : undefined,
        tripDaysMax:
          this.filters.tripDaysMax !== null
            ? this.filters.tripDaysMax
            : undefined,
        groupSizeMin:
          this.filters.groupSizeMin !== null
            ? this.filters.groupSizeMin
            : undefined,
        groupSizeMax:
          this.filters.groupSizeMax !== null
            ? this.filters.groupSizeMax
            : undefined,
        themes:
          // this.filters.themes.length > 0
          //   ? this.filters.themes.join(",")
          //   : undefined,
          this.filters.themes.length > 0
            ? this.filters.themes
                .map((id) => {
                  const item = this.themeOptions.find((o) => o.id === id);
                  return this.$i18n.locale === "zh" ? item.name : item.name_en;
                })
                .join(",")
            : undefined,
        priceMin:
          this.filters.priceMin !== null ? this.filters.priceMin : undefined,
        priceMax:
          this.filters.priceMax !== null ? this.filters.priceMax : undefined,
        sortBy: this.sortBy,
      };
      (params.language = this.$i18n.locale || "zh"),
        // 请求后端接口（分页数据由后端返回）
        this.$post("/travel/get_travel_plan_list", params)
          .then((res) => {
            if (res.data.code === 1) {
              this.routes = res.data.data.list; // 当前页数据（仅本页内容）
              this.totalCount = res.data.data.total; // 总条数（用于分页组件计算总页数）
              if (this.totalCount === 0) {
                this.$message.error(this.$t("noResult"));
              }
            } else {
              this.routes = [];
              this.totalCount = 0;
              this.$message.error(res.data.msg);
            }
          })
          .catch((err) => {
            console.error("分页请求失败：", err);
            this.$message.error(this.$t("loadFailedRetry"));
          });
    },
    // 页码变化时触发（请求对应页数据）
    handleCurrentChange(page) {
      this.currentPage = page; // 更新当前页码
      this.fetchFilteredRoutes(); // 重新请求后端（传递新页码）
    },
    // 每页条数变化时触发（重置页码并请求第1页）
    handleSizeChange(size) {
      this.pageSize = size; // 更新每页条数
      this.currentPage = 1; // 重置为第1页
      this.fetchFilteredRoutes(); // 重新请求后端（传递新的pageSize）
    },
    // 其他方法中涉及分页的地方（如筛选、排序变化）均需重置页码
    handleSearch(keyword) {
      this.searchKeyword = keyword;
      this.currentPage = 1; // 搜索后从第1页开始
      this.fetchFilteredRoutes();
      this.getCityInfo();
    },
    handleCityChange(selectedCity) {
      // 这里处理选中的城市数据
      this.selectedCity = selectedCity;
      this.fetchFilteredRoutes();
      this.getCityInfo();
    },
    handleSearchCleared() {
      this.searchKeyword = "";
      this.fetchFilteredRoutes();
    },
    updateFilter(key, value) {
      if (key === "groupSize") {
        // 处理成团人数筛选
        if (value === null) {
          this.filters.groupSize = null;
          this.filters.groupSizeMin = null;
          this.filters.groupSizeMax = null;
        } else {
          // this.filters.groupSize =
          //   this.$i18n.locale === "zh" ? value.label : value.label_en;
          this.filters.groupSize = value.id;
          this.filters.groupSizeMin = value.min;
          this.filters.groupSizeMax = value.max;
        }
      } else if (key === "tripDays") {
        // 处理行程天数筛选
        if (value === null) {
          this.filters.tripDays = null;
          this.filters.tripDaysMin = null;
          this.filters.tripDaysMax = null;
        } else {
          this.filters.tripDays = value.id;
          // this.filters.tripDays =
          //   this.$i18n.locale === "zh" ? value.name : value.name_en;
          this.filters.tripDaysMin = value.min;
          this.filters.tripDaysMax = value.max;
        }
      } else if (key === "travelType") {
        // 处理出行方式筛选（修复问题的核心部分）
        if (value === null) {
          // 当点击"不限"时，重置相关属性
          this.filters.travelTypeName = null;
          this.filters.travelType = null;
        } else {
          // this.filters.travelTypeName =
          //   this.$i18n.locale === "zh" ? value.name : value.name_en;
          this.filters.travelType = value.id;
        }
      } else {
        this.filters[key] = value;
      }
      this.currentPage = 1; // 筛选条件变化后从第1页开始
      this.fetchFilteredRoutes();
    },
    handleSortChange() {
      this.currentPage = 1; // 排序变化后从第1页开始
      this.fetchFilteredRoutes();
    },
    //获取静态数据
    getStaticData() {
      this.$get("/get_static", {
        type: "travel_modes,day_options,group_size_options,theme_options,price_ranges",
      }).then((res) => {
        if (res.data.code === 1) {
          this.travelTypeOptions = res.data.data.travel_modes;
          this.dayOptions = res.data.data.day_options;
          this.groupSizeOptions = res.data.data.group_size_options;
          this.themeOptions = res.data.data.theme_options;
          this.defaultPriceRanges = res.data.data.price_ranges;
        } else {
          this.$message.error(res.data.msg);
        }
      });
    },
    //获取热门产品
    getHotProducts() {
      const lang = this.$i18n.locale || "zh";
      this.$post("/travel/get_hot_products", {
        language: lang, // 传递当前语种
      }).then((res) => {
        if (res.data.code === 1) {
          this.hotProducts = res.data.data.hotProducts;
        } else {
          this.$message.error(res.data.msg);
        }
      });
    },
    //获取城市介绍
    getCityInfo() {
      this.$get("/travel/get_city_introduction", {
        keyword: this.searchKeyword,
      }).then((res) => {
        if (res.data.code === 1) {
          this.cityInfo = res.data.data;
        } else {
          this.$message.error(res.data.msg);
        }
      });
    },
    // 清除自定义选择状态（仅在输入变化时）
    clearCustomSelection() {
      // 输入变化时，清除预设区间的选中状态，但不触发筛选
      this.activePriceRangeKey = "custom-editing";
    },
    // 选择价格区间
    selectPriceRange(range) {
      if (range === null) {
        // 不限价格
        this.filters.priceMin = null;
        this.filters.priceMax = null;
        this.activePriceRangeKey = null;
      } else {
        // 选择默认区间
        this.filters.priceMin = range.min;
        this.filters.priceMax = range.max;
        this.activePriceRangeKey = range.key;
      }
      this.priceRange = [this.filters.priceMin, this.filters.priceMax]; // 同步输入框
      this.filters.priceRange = [this.filters.priceMin, this.filters.priceMax];
      this.currentPage = 1; // 重置分页
      this.fetchFilteredRoutes(); // 触发接口请求
    },
    // 自定义价格输入变化
    updateCustomPriceRange() {
      // 确保最小值不大于最大值
      const min =
        this.priceRange[0] !== null && this.priceRange[0] !== ""
          ? Number(this.priceRange[0])
          : null;
      const max =
        this.priceRange[1] !== null && this.priceRange[1] !== ""
          ? Number(this.priceRange[1])
          : null;
      if (min > max) {
        this.$message.warning(this.$t("minNotGreaterThanMax"));
        return;
      }
      // 更新筛选条件
      this.filters.priceMin = min;
      this.filters.priceMax = max;
      this.filters.priceRange = [min, max];
      this.activePriceRangeKey = ""; // 取消默认区间选中
      this.currentPage = 1; // 重置分页
      this.fetchFilteredRoutes(); // 触发接口请求
    },
    // 切换主题筛选
    toggleTheme(theme) {
      // if (themeName === "不限") {
      //   this.filters.themes = []; // 清空主题
      // } else {
      //   // 切换选中状态
      //   if (this.filters.themes.includes(themeName)) {
      //     this.filters.themes = this.filters.themes.filter(
      //       (name) => name !== themeName,
      //     );
      //   } else {
      //     this.filters.themes.push(themeName);
      //   }
      // }
      // this.currentPage = 1; // 重置分页
      // this.fetchFilteredRoutes(); // 触发接口请求
      let themes = [...this.filters.themes];

      // 点击不限 → 清空
      if (theme === "不限") {
        this.filters.themes = [];
      } else {
        // 点击主题 → 存 id
        const themeId = theme.id;
        if (themes.includes(themeId)) {
          themes = themes.filter((t) => t !== themeId);
        } else {
          themes.push(themeId);
        }
        this.filters.themes = themes;
      }

      this.currentPage = 1;
      // 触发接口请求
      this.fetchFilteredRoutes();
    },
    // 切换主题展开/收起状态
    toggleThemeExpand() {
      this.isThemeExpanded = !this.isThemeExpanded;
    },
    // 清空所有筛选条件
    clearAllFilters() {
      this.filters = {
        travelType: null,
        tripDays: null,
        themes: [],
        groupSize: null,
        priceMin: null,
        priceMax: null,
        priceRange: [null, null],
      };
      this.searchKeyword = "";
      this.sortBy = "date";
      this.activePriceRangeKey = null;
      this.priceRange = [null, null];
      this.currentPage = 1; // 重置分页
      this.fetchFilteredRoutes(); // 触发接口请求
    },
    // 移除单个筛选条件
    removeFilter(key, value) {
      if (key === "themes") {
        // 移除单个主题
        this.filters.themes = this.filters.themes.filter(
          (name) => name !== value,
        );
      } else if (key === "priceRange") {
        // 移除价格筛选
        this.filters.priceMin = null;
        this.filters.priceMax = null;
        this.filters.priceRange = [null, null];
        this.activePriceRangeKey = null;
        this.priceRange = [null, null];
      } else {
        // 其他筛选条件
        this.filters[key] = null;
      }
      this.currentPage = 1; // 重置分页
      this.fetchFilteredRoutes(); // 触发接口请求
    },
    toRouteDetail(id) {
      this.$router.push({
        path: "/travel/route-detail",
        query: { id: id },
      });
    },
  },
};
</script>

<style scoped>
/* 基础样式 */
.travel-route-list {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}
.filter-section,
.route-item,
.route-image,
.hot-products,
.city-info {
  border-radius: 8px;
}
/* 筛选区域样式 */
.filter-section {
  background-color: #fff;
  padding: 15px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
/* 筛选组样式 */
.filter-group {
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px solid #f5f7fa;
}
.filter-group:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}
/* 筛选行样式 - 左右结构 */
.filter-row {
  display: flex;
  align-items: flex-start;
}
.filter-label-col {
  flex: 0 0 80px; /* 标签列宽度固定 */
  padding-right: 10px;
}
.filter-options-col {
  flex: 1;
}
.filter-label {
  font-weight: 500;
  color: #333;
  font-size: 14px;
  text-align: right; /* 标签右对齐 */
  padding-top: 5px; /* 与选项垂直居中对齐 */
}
/* 通用选项样式 */
.filter-options {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.option-item {
  padding: 6px 12px;
  background-color: #f5f7fa;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 13px;
}
.option-item:hover {
  background-color: #e6f7ff;
}
.option-item.selected {
  background-color: #409eff;
  color: #fff;
}
/* 主题筛选器特殊样式 */
.theme-filter .filter-options {
  overflow: hidden;
  transition: max-height 0.3s ease;
  width: 95%;
}
.theme-filter .filter-options.expanded {
  max-height: none;
}
.theme-options-wrapper {
  position: relative;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}
.toggle-theme-btn {
  color: #409eff;
  font-size: 12px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-top: 8px;
}
.group-size-tag {
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: rgba(64, 158, 255, 0.8);
  color: #fff;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
}
/* 显示更多按钮样式 */
.show-more {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  text-align: center;
  color: #409eff;
  cursor: pointer;
  font-size: 12px;
  padding: 3px 0;
  background: linear-gradient(
    to top,
    rgba(255, 255, 255, 0.95),
    rgba(255, 255, 255, 0)
  );
}
.show-more i {
  font-size: 11px;
  margin-left: 2px;
}
/* 价格筛选项特殊样式 */
.price-range {
  display: flex;
  align-items: center;
  gap: 10px;
}
.price-input {
  width: 100px !important;
  /* height: 32px !important; */
}
.price-unit,
.price-separator {
  font-size: 14px;
  color: #999;
  margin: 0px 10px;
}
/* 筛选操作按钮 */
.filter-actions {
  margin-top: 10px;
  text-align: right;
}
.clear-filters {
  background-color: transparent;
  border: 1px solid #409eff;
  color: #409eff;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  transition: all 0.2s;
  font-size: 13px;
}
.clear-filters:hover {
  background-color: #409eff;
  color: white;
}
.clear-filters:disabled {
  color: #ccc;
  border-color: #ccc;
  cursor: not-allowed;
  background-color: transparent;
}
/* 已选筛选条件标签 */
.active-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px dashed #eee;
}
.active-label {
  color: #666;
  font-size: 13px;
  line-height: 24px;
}
/* 主要内容区域样式 */
.main-content {
  display: flex;
  gap: 20px;
  padding: 4px;
}
.list-section {
  flex: 3;
}
.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}
.result-count {
  font-size: 16px;
  font-weight: 500;
}
.sort-container {
  display: flex;
  align-items: center;
  gap: 10px;
}
.sort-select {
  width: 180px;
}
.route-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}
.route-item {
  display: flex;
  background-color: #fff;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s, box-shadow 0.3s;
  padding: 15px;
  cursor: pointer;
}
.route-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}
.route-image {
  position: relative;
  width: 220px;
  height: 160px;
  overflow: hidden;
}
.route-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s;
}
.route-image:hover img {
  transform: scale(1.05);
}
.route-tag {
  position: absolute;
  top: 10px;
  left: 10px;
  /* background-color: rgba(0, 0, 0, 0.6); */
  color: #fff;
  /* padding: 2px 8px; */
  border-radius: 4px;
  font-size: 12px;
}
.route-info {
  flex: 1;
  margin-left: 15px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
.route-title {
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 8px;
  color: #333;
  transition: color 0.3s;
}
.route-title:hover {
  color: #409eff;
}
.route-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  font-size: 13px;
  color: #666;
  margin-bottom: 8px;
}
.route-features {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-bottom: 8px;
}
.route-price {
  font-size: 18px;
  font-weight: 700;
  color: #f56c6c;
  margin-bottom: 5px;
}
.price-value {
  font-size: 20px;
}
.route-stats {
  display: flex;
  gap: 15px;
  font-size: 13px;
  color: #999;
}
.no-result {
  padding: 50px 0;
  text-align: center;
}
.pagination-container {
  margin-top: 30px;
  text-align: center;
}
/* 侧边栏样式 */
.sidebar {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.city-info,
.hot-products {
  background-color: #fff;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
.section-title {
  padding: 12px 15px;
  border-bottom: 1px solid #eee;
  font-size: 16px;
  font-weight: 500;
}
.city-image {
  height: 180px;
  overflow: hidden;
}
.city-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.city-description {
  padding: 15px;
  font-size: 14px;
  line-height: 1.6;
  color: #666;
}
.hot-products {
  padding-bottom: 15px;
}
.hot-product-item {
  display: flex;
  padding: 10px 15px;
  gap: 10px;
  border-bottom: 1px solid #eee;
  transition: background-color 0.3s;
  cursor: pointer;
}
.hot-product-item:last-child {
  border-bottom: none;
}
.hot-product-item:hover {
  background-color: #f5f7fa;
}
.hot-product-rank {
  width: 20px;
  height: 20px;
  background-color: #409eff;
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  flex-shrink: 0;
}
.hot-product-info {
  flex: 1;
}
.hot-product-title {
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 5px;
  color: #333;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.hot-product-route {
  font-size: 12px;
  color: #999;
  margin-bottom: 5px;
}
.hot-product-price {
  font-size: 14px;
  font-weight: 600;
  color: #f56c6c;
}
/* 价格筛选样式调整 */
.price-filter-container {
  display: flex;
  align-items: center;
  gap: 15px;
  flex-wrap: wrap;
}
.price-ranges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.price-range-option {
  padding: 4px 12px;
  background-color: #f5f7fa;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;
  white-space: nowrap;
}
.price-range-option:hover {
  background-color: #e6f7ff;
}
.price-range-option.selected {
  background-color: #409eff;
  color: #fff;
}
.custom-price-input {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 0 0 auto;
}
/* 响应式调整 */
@media (max-width: 992px) {
  .main-content {
    flex-direction: column;
  }
  .sidebar {
    flex-direction: row;
  }
  .city-info,
  .hot-products {
    flex: 1;
  }
}
@media (max-width: 768px) {
  .main-content {
    padding: 0px;
  }
  .travel-route-list {
    padding: 10px;
  }
  .filter-section {
    padding: 10px;
  }
  .filter-group {
    margin-bottom: 5px;
    padding-bottom: 5px;
  }
  .option-item {
    padding: 4px 8px;
  }
  .route-item {
    flex-direction: column;
    padding: 10px;
  }
  .route-info {
    margin-left: 0px;
  }
  .route-image {
    width: 100%;
    height: 200px;
    margin-bottom: 10px;
  }
  .sidebar {
    flex-direction: column;
  }
  /* 移动端调整为上下结构 */
  .filter-row {
    flex-direction: column;
  }
  .filter-label-col {
    flex: 1;
    width: 100%;
    padding-right: 0;
    margin-bottom: 8px;
  }
  .filter-label {
    text-align: left;
    padding-top: 0;
  }
  .toggle-theme-btn {
    margin-top: 0;
    margin-left: 10px;
  }
  .theme-filter .filter-options.expanded {
    max-height: none;
  }
  .price-filter-container {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  .custom-price-input {
    width: 100%;
  }
}
</style>
